export enum Section {
    RBC = 'RBC',
    IRON = 'Iron',
    WBC = 'WBC',
    PLATELETS = 'Platelets',
    GLUCOSE = 'Glucose',
    KIDNEY = 'Kidney',
    // Pattern-driven section: every marker it reads is owned by another
    // section (protein/albumin/globulin by Proteins, BUN by Kidney, the
    // minerals by Nutrients/Electrolytes, the differential by WBC). It
    // borrows a display set so it can carry a Results Overview pill, while
    // detection reads whatever a pattern needs — the same split glucose
    // already uses for its 73xxx add-ons (ALT, cortisol, potassium, amylase
    // are read but not displayed in the Glucose section).
    DIGESTION = 'Digestion',
    NUTRIENTS = 'Nutrients',
    ELECTROLYTES = 'Electrolytes',
    PROTEINS = 'Proteins',
    LIVER_BILIARY = 'Liver / Biliary',
    LIPIDS = 'Lipids',
    INFLAMMATION_OXIDATIVE_STRESS = 'Inflammation / Oxidative Stress',
    THYROID = 'Thyroid',
    SEX_HORMONE = 'Hormones',
    STRESS_ADRENAL = 'Stress & Adrenal Balance',
    PROSTATE = 'Prostate',
    IMMUNE_INFLAMMATORY = 'Immune & Inflammatory Burden Index (I³)',
    ROOT_CAUSE_FRAMEWORK = 'Root Cause Framework',
    ABOVE_OPTIMAL = 'Above Optimal',
    BELOW_OPTIMAL = 'Below Optimal',
    NEEDS_ATTENTION = 'Alarm Range Markers',
    WORTH_DISCUSSING = 'Outside Lab Range Markers',
    FINE_TUNE = 'Outside Optimal Range Markers',
    MARKERS_OUTSIDE_OPTIMAL = 'Markers Outside Optimal',
    RESULTS_OVERVIEW = 'Results Overview',
    SUPPLEMENTS = 'Supplements',
    NOTES = 'Notes',
    // The "Practitioner Notes" tab of the Insights card, and the only note in
    // the app that is never printed in the PDF.
    //
    // The AI raw-data export answers in two parts (see aiReviewPrompt.ts): a
    // clinical read for the practitioner and a plain-language summary for the
    // client. Both used to land in NOTES, which is the note "Export to PDF
    // with notes" prints — so the half written with raw values, alarm
    // references and marker-level reasoning was one forgotten paste away from
    // the client's document.
    //
    // Nothing prints this. bloodwork-analysis-pdf.tsx reads NOTES alone for
    // its Insights card, and the consolidated Section Notes panel walks the
    // SECTION_HEADINGS allowlist, which is body-system sections only. Both
    // exclusions are covered by practitioner-insights-private.test.tsx — if
    // you are adding a new surface that renders notes, that test is the one
    // that will tell you that you have to exclude this.
    PRACTITIONER_INSIGHTS = 'Practitioner Insights',
    // Pseudo-section holding the practitioner's edited override for the
    // consolidated "Section Notes" panel (each body-system section's own Notes
    // are gathered under this one panel near the report bottom, mirroring how
    // SUPPLEMENTS consolidates the per-section Supplements tabs).
    SECTION_NOTES = 'Section Notes',
    // The practitioner's curated client list: the supplements they ticked in
    // the by-system tables, printed for the client as name, description and
    // dose with no patterns or systems attached. A real section rather than a
    // pseudo-key like MVX_SECTION, because it owns a card, an "Include in PDF"
    // checkbox and a per-test note (which stores the products EXCLUDED from the
    // list — see recommended-supplements-panel.tsx for why the storage is
    // inverted).
    RECOMMENDED_SUPPLEMENTS = 'Recommended Supplements',
    BMI = 'BMI',
    // Markers the practitioner typed straight onto a single test because
    // LabSmarts does not ship them yet (Bloodwork.customMarkers). Renders as a
    // plain reference-range section — no insights, root causes, supplements,
    // Results Overview pill or Markers Outside Optimal participation.
    //
    // The title is client-facing: this section lands in the PDF the client
    // reads. It has been renamed three times, and each name was dropped for a
    // reason worth not relearning:
    //
    //   "Markers Added by the User"   (through 2026-08-10) meant nothing to a
    //                                 client.
    //   "Additional Markers"          (briefly) collided with the Add Test
    //                                 Results card of that name for the shipped
    //                                 amylase/bilirubin/ceruloplasmin panel.
    //   "Practitioner-Added Markers"  (through 2026-08-22) named the AUTHOR
    //                                 rather than the contents. Beside nine
    //                                 headings that carry no such qualifier it
    //                                 read to the client as LabSmarts declining
    //                                 to stand behind that part of their
    //                                 practitioner's report — the same reason
    //                                 the "(Practitioner-Added Section)" suffix
    //                                 went (see practitionerSectionTitle).
    //
    // "Other Lab Results" describes the contents, names no specimen (a marker
    // filed here can be organic acids, stool or hair, which is why the
    // comparison report's title dropped "Blood Work" too) and promises no
    // analysis this section does not do: it has no Insights, no Results
    // Overview pill and no Markers Outside Optimal participation.
    //
    // Renaming this is a data migration, not a string edit — see
    // LEGACY_SECTION_NAMES below.
    CUSTOM_MARKERS = 'Other Lab Results'
}

/**
 * Display names a section used to print under, for every section that has been
 * renamed. Section VALUES are persisted — in UserProfile.excludedFromPdfSections,
 * and as the `section` on each per-test BloodworkNote — so a rename orphans
 * whatever was stored under the old string. Left alone, a practitioner who
 * turned a section off would find it back in the client's PDF, and a note they
 * typed would read as blank.
 *
 * Consulted by UserProfile's constructor (which rewrites the stored list) and
 * by BloodworkNotes.findNote (which falls back on read, since per-test notes
 * are not rewritten on load). Add to this list on any future rename; do not
 * delete from it, because the old rows never expire.
 *
 * Practitioner-named sections are NOT here: their keys are frozen at the
 * literal that was current when they shipped, so a rename cannot reach them.
 * See practitionerSectionStateKey.
 */
export const LEGACY_SECTION_NAMES: { [section: string]: string[] } = {
  [Section.CUSTOM_MARKERS]: [
    'Markers Added by the User',
    'Additional Markers',
    'Practitioner-Added Markers',
  ],
};

/** The display names `section` used to carry, or an empty list. */
export function legacyNamesFor(section: string): string[] {
  return LEGACY_SECTION_NAMES[section] || [];
}
